import { CompetitionTeamRoundFlight } from './../../data/mygolf.data';
import { CommonPullupMenuComponent } from './../pages/modal-screens/common-pullup-menu/common-pullup-menu.component';
import { Injectable, Injector } from '@angular/core';
import { Router, NavigationExtras } from '@angular/router';
import {
  AlertController,
  LoadingController,
  ModalController,
  Platform,
  ToastController,
} from '@ionic/angular';
import { Observable, Subscription, forkJoin, of, firstValueFrom } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { PlayerHomeInfo, PlayerInfo } from 'src/data/player-data';
import {
  CompetitionData,
  CompetitionMatch,
  CompetitionMatchParticipant,
  CompetitionInfo,
  CompetitionDetails,
  PlainScoreCard, // Assuming PlainScoreCard is also in mygolf.data
} from 'src/data/mygolf.data';

import { PlayerHomeDataService } from 'src/redux/player-home/player-home-data-service';
import { PlayerHomeActions } from 'src/redux/player-home/player-home-actions';
import { CurrentScorecardDataService } from 'src/redux/scorecard/current-scorecard-data-service';
import { CurrentScorecardActions } from 'src/redux/scorecard/current-scorecard-actions';
import { CompetitionService } from 'src/providers/competition-service/competition-service';
import { ConnectionService } from 'src/providers/connection-service/connection-service';
import { ClubFlightService } from 'src/providers/club-flight-service/club-flight-service';
import { MessageDisplayUtil } from 'src/message-display-utils';
import { ScorecardService } from 'src/providers/scorecard-service/scorecard-service';
import { mygolfUtils } from 'src/custom/mygolf2u-utils';

@Injectable({
  providedIn: 'root',
})
export class CompetitionFlowService {
  private playerHomeInfo: PlayerHomeInfo | null = null;
  private player$: Observable<PlayerInfo>;
  private multipleComps: Observable<boolean>;
  private normalGameOn: Observable<boolean>;
  private competitionOn: Observable<boolean>;
  private activeCompsToday: Observable<number>;
  private singleActiveComp: Observable<CompetitionInfo>;
  private playerHomeInfo$: Observable<PlayerHomeInfo>;

  private pageLoader: HTMLIonLoadingElement | null = null;

  private playCompClicked = false;
  private timer: any;
  private compClickedCounter = 0;

  constructor(
    private router: Router,
    private playerHomeService: PlayerHomeDataService,
    private currentScorecardService: CurrentScorecardDataService,
    private competitionService: CompetitionService,
    private flightService: ClubFlightService,
    private loadingCtl: LoadingController,
    private alertCtrl: AlertController,
    private toastCtl: ToastController,
    private modalCtl: ModalController,
    private platform: Platform,
    private connService: ConnectionService,
    private scorecardService: ScorecardService,
    private playerHomeActions: PlayerHomeActions,
    private currentScorecardActions: CurrentScorecardActions,
    private injector: Injector
  ) {
    this.playerHomeInfo$ = this.playerHomeService.playerHomeInfo();
    this.player$ = this.playerHomeInfo$.pipe(
      filter(Boolean),
      map((playerHome: PlayerHomeInfo) => playerHome.player)
    );
    this.multipleComps = this.playerHomeInfo$.pipe(
      filter(Boolean),
      map(
        (homeInfo: PlayerHomeInfo) =>
          homeInfo.compsActiveToday && homeInfo.compsActiveToday.length > 1
      )
    );
    this.normalGameOn = this.currentScorecardService.normalGameScoring();
    this.competitionOn = this.currentScorecardService.competitionScoring();
    this.activeCompsToday = this.playerHomeInfo$.pipe(
      filter(Boolean),
      map((homeInfo: PlayerHomeInfo) =>
        homeInfo.compsActiveToday ? homeInfo.compsActiveToday.length : 0
      )
    );
    this.singleActiveComp = this.playerHomeInfo$.pipe(
      filter(Boolean),
      map((homeInfo: PlayerHomeInfo) => {
        if (homeInfo.compsActiveToday && homeInfo.compsActiveToday.length === 1) {
          return homeInfo.compsActiveToday[0];
        }
        return null;
      })
    );
  }

  private async initPageLoader(message = '') {
    this.pageLoader = await this.loadingCtl.create({
      cssClass: 'custom-loading',
      message,
      backdropDismiss: false,
      duration: 2500,
      showBackdrop: true,
    });
  }

  async handleCompetitionEntry(
    oldEventList: boolean = true,
    isClubSpecific: boolean,
    isClubSpecificCompetition: (isClubSpecific: boolean) => any[],
    mg2uUtil: mygolfUtils
  ) {
    return new Promise(async (resolve, reject) => {
      this.playerHomeInfo$.pipe(take(1)).subscribe((homeInfo: PlayerHomeInfo) => {
        this.playerHomeInfo = homeInfo;
      });

      if (!this.checkAndDisplayConnection()) {
        return false;
      }

      console.debug('competition entry point ', this.playerHomeInfo);

      if (
        !this.playerHomeInfo ||
        !this.playerHomeInfo?.compsActiveToday ||
        this.playerHomeInfo?.compsActiveToday.length === 0
      ) {
        console.debug('No active competitions found');
        this.playCompClicked = false;
        // if (oldEventList) {
        //   this.router.navigate(['competition-list']);
        // } else {
        //   this.router.navigate(['tournaments-view']);
        // }
        resolve(false);
      }

      if (this.playCompClicked) {
        if (this.timer && new Date().getTime() - this.timer < 3000) {
          console.debug('Competition entry blocked - too soon after previous click');

          resolve(false);
        }
      }
      this.timer = new Date().getTime();
      this.playCompClicked = true;

      let _multipleComps: boolean;
      this.multipleComps.pipe(filter(Boolean)).subscribe((a: boolean) => {
        if (isClubSpecificCompetition(isClubSpecific).length > 1) {
          _multipleComps = a;
        } else if (!isClubSpecific) {
          _multipleComps = a;
        } else {
          _multipleComps = false;
        }
      });

      let _normalGameOn: boolean;
      this.normalGameOn.pipe(filter(Boolean)).subscribe((a: boolean) => {
        _normalGameOn = a;
      });

      let _activeCompsToday: number;
      this.activeCompsToday.pipe(filter(Boolean)).subscribe((a: number) => {
        if (isClubSpecificCompetition(isClubSpecific).length > 0) {
          _activeCompsToday = a;
        } else if (!isClubSpecific) {
          _activeCompsToday = a;
        } else {
          _activeCompsToday = 0;
        }
      });

      let _singleActiveComp: CompetitionInfo | null;
      this.singleActiveComp.pipe(filter(Boolean)).subscribe((a: CompetitionInfo) => {
        if (isClubSpecificCompetition(isClubSpecific).length > 0) {
          _singleActiveComp = a;
        } else if (!isClubSpecific) {
          _singleActiveComp = a;
        } else {
          _singleActiveComp = null;
        }
      });

      let _continueMatchPlay = true;
      let _continueScramble = true;
      let _compId = 0;
      this.playerHomeInfo$.pipe(take(1)).subscribe((homeInfo: PlayerHomeInfo) => {
        if (homeInfo?.compsActiveToday?.length === 1) {
          _compId = homeInfo.compsActiveToday[0].competitionId;
        }
      });

      if (_multipleComps) {
        this.navigateToCompetitionSelection();
        return;
      }
      await this.checkWhetherCompMatch(
        this.playerHomeInfo?.compsActiveToday[0],
        isClubSpecific,
        mg2uUtil
      ).then(async (cont: boolean) => {
        _continueMatchPlay = cont;
        if (_continueMatchPlay) {
          // to add check whether scramble scoring for all players
          await this.checkWhetherCompAllScores(
            this.playerHomeInfo?.compsActiveToday[0],
            isClubSpecific,
            mg2uUtil
          ).then(async (cont: boolean) => {
            _continueScramble = cont;
            if (_continueScramble) {
              return this.handleNormalScoringFlow(
                _normalGameOn,
                _singleActiveComp,
                _activeCompsToday,
                _multipleComps
              );
            }
          });
        }
        // else {
        //   return this.handleNormalScoringFlow(
        //     _normalGameOn,
        //     _singleActiveComp,
        //     _activeCompsToday,
        //     _multipleComps
        //   );
        // }
      });
    });
  }

  handleNormalScoringFlow(normalGameOn, singleActiveComp, activeCompsToday, multipleComps) {
    return new Promise(async (resolve, reject) => {
      // Skipped for multipleComps on purpose: the selection page owns that decision.
      if (!multipleComps && this.playerHomeInfo?.compsActiveToday?.length) {
        const compId = this.playerHomeInfo.compsActiveToday[0].competitionId;
        const liveScoringBlocked = await this.isLiveScoringBlocked(compId);
        if (liveScoringBlocked) {
          MessageDisplayUtil.showErrorToast(
            'Live Scoring is Disabled for this Competition',
            this.platform,
            this.toastCtl,
            5000,
            'bottom'
          );
          this.playCompClicked = false;
          resolve(false);
          return;
        }
      }

      if (normalGameOn && (singleActiveComp || activeCompsToday || multipleComps)) {
        const overrideResult = await this.promptForNormalGameOverride();
        if (overrideResult) {
          console.debug(
            'overriding results ',
            overrideResult,
            normalGameOn,
            singleActiveComp,
            activeCompsToday,
            multipleComps,
            this.playerHomeInfo
          );
          if (activeCompsToday || multipleComps) {
            if (multipleComps) {
              this.navigateToCompetitionSelection();
            } else {
              await this.initPageLoader('Getting scorecard. Please wait...');
              if (this.pageLoader) {
                this.pageLoader.present();
              }
              await this.navigateToScoring(
                this.playerHomeInfo.compsActiveToday[0].competitionId,
                this.playerHomeInfo.player.playerId
              );
            }
            resolve(true);
          } else {
            resolve(false);
            // this.router.navigate(['tournaments-view']);
          }
        }
      } else {
        if (singleActiveComp || activeCompsToday || multipleComps) {
          console.debug(
            'no normal game',
            normalGameOn,
            singleActiveComp,
            activeCompsToday,
            multipleComps,
            this.playerHomeInfo
          );
          if (multipleComps) {
            this.navigateToCompetitionSelection();
          } else {
            await this.initPageLoader('Getting scorecard. Please wait...');
            if (this.pageLoader) {
              this.pageLoader.present();
            }
            await this.navigateToScoring(
              this.playerHomeInfo.compsActiveToday[0].competitionId,
              this.playerHomeInfo.player.playerId
            );
          }
          resolve(true);
        } else {
          // this.router.navigate(['tournaments-view']);
          resolve(false);
        }
      }
    });
  }

  private async promptForNormalGameOverride(): Promise<boolean> {
    return new Promise(async resolve => {
      const modal = await this.modalCtl.create({
        mode: 'ios',
        component: CommonPullupMenuComponent,
        cssClass: 'self-adjustable-modal',
        componentProps: {
          mode: 'negative-confirmation',
          headerTitle: 'Active Scorecard Found',
          confirmMessage:
            `You have an active scorecard for a normal round. Tapping <b>Yes</b> will remove your active scorecard.<br><br>` +
            `Tap <b>No</b> if you want to first save your active Scorecard.`,
        },
      });
      await modal.present().then(() => {
        const height = this.platform.height() - 250;
        const innerContent = document.getElementsByClassName('inner-content')[0] as HTMLElement;
        if (innerContent) {
          innerContent.style.maxHeight = `${height}px`;
        }
      });
      modal.onDidDismiss().then((data: any) => {
        console.debug('pullup dismiss', data);
        if (data?.data) {
          resolve(true);
        } else {
          resolve(false);
        }
        this.playCompClicked = false;
      });
    });
  }

  private navigateToCompetitionSelection() {
    this.router.navigate(['competition-scoring'], {
      state: {
        homeInfo: this.playerHomeInfo,
        fromMultiple: true,
        player: this.playerHomeInfo?.player,
      },
    });
    this.playCompClicked = false;
  }

  async oldNavigateToScoring(competitionId: number, playerId: number) {
    console.debug('Navigating to scoring for competition:', competitionId, 'player:', playerId);

    let existingScorecard: PlainScoreCard | null = null;
    try {
      existingScorecard = await firstValueFrom(
        this.scorecardService.getCurrentScorecard().pipe(take(1))
      );
      console.debug('Existing scorecard check:', existingScorecard);
    } catch (error) {
      console.error('Error fetching current scorecard:', error);
      this.onDismissPageLoader();
      this.playCompClicked = false;
      return;
    }

    if (
      existingScorecard &&
      !ScorecardService.isAllScoresEmpty(existingScorecard, playerId) &&
      existingScorecard?.competition &&
      !existingScorecard?.finished &&
      existingScorecard?.dirty &&
      existingScorecard?.competitionId === competitionId
    ) {
      console.debug('Found valid existing scorecard for competition:', competitionId);
      try {
        const comp = await firstValueFrom(
          this.competitionService.getCompetitionInfo(competitionId)
        );
        if (comp) {
          // && this.playCompClicked
          this.playCompClicked = false;
          this.currentScorecardActions.setScoringPlayer(playerId);

          const navigationExtras: NavigationExtras = {
            state: {
              scorecard: existingScorecard,
              competition: comp,
              currentPlayer: this.playerHomeInfo?.player,
              compId: comp?.competitionId || competitionId,
            },
          };
          await this.router.navigate(['gameround-scoring'], navigationExtras);
          this.onDismissPageLoader();
        }
      } catch (error) {
        console.error('Error getting competition info for existing scorecard:', error);
        this.onDismissPageLoader();
        this.playCompClicked = false;
      }
    } else {
      console.debug(
        'No valid existing scorecard found, fetching new one for competition:',
        competitionId
      );
      try {
        const loader = await this.loadingCtl.getTop();
        this.playerHomeActions.getCompetitionScoring(competitionId, playerId, loader);

        let retryCount = 0;
        const maxRetries = 10;
        const retryInterval = 300;

        const checkForScorecard = async () => {
          try {
            const newScorecard: PlainScoreCard | null = await firstValueFrom(
              this.scorecardService.getCurrentScorecard().pipe(take(1))
            );
            console.debug('Checking for scorecard info:', this.playCompClicked, newScorecard);

            if (newScorecard && newScorecard.competitionId === competitionId) {
              const comp = await firstValueFrom(
                this.competitionService.getCompetitionInfo(competitionId)
              );
              console.debug(
                'Checking for scorecard info [1]:',
                this.playCompClicked,
                comp,
                competitionId,
                playerId
              );
              if (comp) {
                this.playCompClicked = false;
                this.currentScorecardActions.setScoringPlayer(playerId);

                const navigationExtras: NavigationExtras = {
                  state: {
                    scorecard: newScorecard,
                    competition: comp,
                    currentPlayer: this.playerHomeInfo?.player,
                    compId: comp?.competitionId || competitionId,
                  },
                };

                await this.router.navigate(['gameround-scoring'], navigationExtras);
                this.onDismissPageLoader();
              }
            } else if (retryCount < maxRetries && this.playCompClicked) {
              retryCount++;
              console.debug(`Scorecard not ready, retry ${retryCount}/${maxRetries}`);
              setTimeout(checkForScorecard, retryInterval);
            } else {
              console.debug('No scorecard retrieved after retries or user cancelled');
              this.playCompClicked = false;
              this.onDismissPageLoader();
            }
          } catch (error) {
            console.error('Error during scorecard check/navigation:', error);
            this.playCompClicked = false;
            this.onDismissPageLoader();
          }
        };

        setTimeout(checkForScorecard, 500);
      } catch (error) {
        console.error('Error initiating competition scoring:', error);
        this.onDismissPageLoader();
        this.playCompClicked = false;
      }
    }
  }

  // Updated navigateToScoring method
  async navigateToScoring(competitionId: number, playerId: number) {
    console.debug('Navigating to scoring for competition:', competitionId, 'player:', playerId);

    try {
      // Check for existing scorecard
      const existingScorecard = await firstValueFrom(
        this.scorecardService.getCurrentScorecard().pipe(take(1))
      );

      console.debug('Existing scorecard check:', existingScorecard);

      // If valid existing scorecard exists, navigate directly
      if (
        existingScorecard &&
        !ScorecardService.isAllScoresEmpty(existingScorecard, playerId) &&
        existingScorecard?.competition &&
        !existingScorecard?.finished &&
        existingScorecard?.dirty &&
        existingScorecard?.competitionId === competitionId
      ) {
        console.debug('Found valid existing scorecard for competition:', competitionId);
        await this.navigateWithScorecard(existingScorecard, competitionId, playerId);
        return;
      }

      // No valid existing scorecard - fetch new one
      console.debug(
        'No valid existing scorecard found, fetching new one for competition:',
        competitionId
      );

      const loader = await this.loadingCtl.getTop();

      // Wait for the scorecard to be fetched and stored
      const newScorecard = await firstValueFrom(
        this.playerHomeActions
          .getNewCompetitionScoring(competitionId, playerId, loader)
          .pipe(take(1))
      );

      console.debug('New scorecard retrieved:', newScorecard);

      if (newScorecard && newScorecard.competitionId === competitionId) {
        await this.navigateWithScorecard(newScorecard, competitionId, playerId);
      } else {
        console.error('Failed to retrieve scorecard for competition:', competitionId);
        this.playCompClicked = false;
        this.onDismissPageLoader();
      }
    } catch (error) {
      console.error('Error during scorecard navigation:', error);
      this.playCompClicked = false;
      this.onDismissPageLoader();
    }
  }

  // Helper method to reduce duplication
  private async navigateWithScorecard(
    scorecard: PlainScoreCard,
    competitionId: number,
    playerId: number
  ) {
    try {
      const comp = await firstValueFrom(this.competitionService.getCompetitionInfo(competitionId));

      if (!comp) {
        console.error('Competition info not found for ID:', competitionId);
        this.playCompClicked = false;
        this.onDismissPageLoader();
        return;
      }

      this.playCompClicked = false;
      this.currentScorecardActions.setScoringPlayer(playerId);

      const navigationExtras: NavigationExtras = {
        state: {
          scorecard: scorecard,
          competition: comp,
          currentPlayer: this.playerHomeInfo?.player,
          compId: comp?.competitionId || competitionId,
        },
      };

      await this.router.navigate(['gameround-scoring'], navigationExtras);
      this.onDismissPageLoader();
    } catch (error) {
      console.error('Error navigating with scorecard:', error);
      this.playCompClicked = false;
      this.onDismissPageLoader();
    }
  }

  async checkWhetherCompMatch(
    comp: any,
    isClubSpecific: boolean,
    mg2uUtil: mygolfUtils
  ): Promise<boolean> {
    return new Promise(async resolve => {
      const _compId = comp?.competitionId;
      if (!_compId) {
        resolve(true);
        return;
      }
      let hasActiveComps = false;
      this.activeCompsToday.pipe(take(1)).subscribe(count => {
        hasActiveComps = count > 0;
      });

      if (!hasActiveComps) {
        resolve(true);
        return;
      }

      try {
        const compData: CompetitionData = await firstValueFrom(
          this.flightService.getCompetitionDetails(Number(_compId))
        );
        console.debug(
          'get comp data ',
          compData,
          compData?.addlSettings,
          compData?.teamEvent,
          compData?.matchPlay
        );
        if (compData?.teamEvent && compData?.matchPlay) {
          await this.initPageLoader('Getting scorecard. Please wait...');
          if (!compData?.addlSettings?.matchPlayDetailedScoring) {
            if (this.pageLoader) {
              this.pageLoader.present();
            }
            const playerMatches = await this.getCompMatches(compData?.id);
            if (playerMatches?.length) {
              if (playerMatches.length > 0) {
                this.playCompClicked = false;
              }
              this.handleMatchplayFlow(compData, this.playerHomeInfo?.player?.playerId);
              resolve(false);
            } else {
              resolve(true);
            }
          } else {
            resolve(true);
          }
        } else {
          resolve(true);
        }
      } catch (error) {
        console.error('Error in checkWhetherCompMatch:', error);
        resolve(true);
      }
    });
  }

  async checkWhetherCompAllScores(
    comp: any,
    isClubSpecific: boolean,
    mg2uUtil: mygolfUtils
  ): Promise<boolean> {
    return new Promise(async resolve => {
      const _compId = comp?.competitionId;
      if (!_compId) {
        resolve(true);
        return;
      }
      let hasActiveComps = false;
      this.activeCompsToday.pipe(take(1)).subscribe(count => {
        hasActiveComps = count > 0;
      });

      if (!hasActiveComps) {
        resolve(true);
        return;
      }

      try {
        const compData: CompetitionData = await firstValueFrom(
          this.flightService.getCompetitionDetails(Number(_compId))
        );
        console.debug(
          'get comp data ',
          compData,
          compData?.addlSettings,
          compData?.teamEvent,
          compData?.matchPlay
        );
        if (compData?.teamEvent) {
          await this.initPageLoader('Getting scorecard. Please wait...');
          if (
            // ! to be updated with proper mobile server code
            // compData?.addlSettings?.teamEventRules?.allPlayerScoresRequired
            compData?.addlSettings?.teamEventRules?.teamTournamentFormat !== 'Standard'
          ) {
            if (this.pageLoader) {
              this.pageLoader.present();
            }
            const teamFlightScores = await this.getCompTeamRoundScores(compData?.id);
            // const playerMatches = await this.getCompMatches(compData?.id);
            if (teamFlightScores) {
              this.playCompClicked = false;
              this.handleScrambleScoringFlow(compData, this.playerHomeInfo?.player?.playerId);
              resolve(false);
            }
            // if (playerMatches?.length) {
            //   if (playerMatches.length > 0) {
            //     this.playCompClicked = false;
            //   }
            //   this.handleMatchplayFlow(compData, this.playerHomeInfo?.player?.playerId);
            //   resolve(false);
            // }
            else {
              resolve(true);
            }
          } else {
            resolve(true);
          }
        } else {
          resolve(true);
        }
      } catch (error) {
        console.error('Error in checkWhetherCompMatch:', error);
        resolve(true);
      }
    });
  }

  async handleMatchplayFlow(competitionData: CompetitionData, playerId: number) {
    console.debug('Handling matchplay flow for competition:', competitionData?.id);
    await this.initPageLoader('Getting match details. Please wait...');
    if (this.pageLoader) {
      this.pageLoader.present();
    }

    try {
      const matches = await this.getCompMatches(competitionData.id);
      if (matches && matches.length > 0) {
        this.onDismissPageLoader();
        const navigationExtras: NavigationExtras = {
          state: {
            compData: competitionData,
            playerMatch: matches[0],
            compId: competitionData?.id,
            currentPlayer: this.playerHomeInfo?.player,
            editingScorecard: true,
          },
        };
        await this.router.navigate(['matchplay-scoring'], navigationExtras);
      } else {
        MessageDisplayUtil.showErrorToast(
          'No matchplay data found.',
          this.platform,
          this.toastCtl,
          3000,
          'bottom'
        );
      }
    } catch (error) {
      console.error('Error handling matchplay flow:', error);
      MessageDisplayUtil.showErrorToast(
        'Failed to load matchplay data.',
        this.platform,
        this.toastCtl,
        3000,
        'bottom'
      );
    } finally {
      this.onDismissPageLoader();
      this.playCompClicked = false;
    }
  }

  // handleScrambleScoringFlow
  async handleScrambleScoringFlow(competitionData: CompetitionData, playerId: number) {
    console.debug('Handling matchplay flow for competition:', competitionData?.id);
    await this.initPageLoader('Getting match details. Please wait...');
    if (this.pageLoader) {
      this.pageLoader.present();
    }

    try {
      const teamFlightScores = await this.getCompTeamRoundScores(competitionData?.id);
      const competition = await firstValueFrom(
        this.competitionService.getCompetitionInfo(competitionData?.id)
      );
      console.debug('comp flow : ', teamFlightScores, competition, competitionData);
      // const matches = await this.getCompMatches(competitionData.id);
      if (teamFlightScores) {
        this.onDismissPageLoader();
        const navigationExtras: NavigationExtras = {
          state: {
            compData: competitionData,
            competition: competition,
            // playerMatch: matches[0],
            flightScores: teamFlightScores,
            compId: competitionData?.id,
            currentPlayer: this.playerHomeInfo?.player,
            editingScorecard: true,
          },
        };
        await this.router.navigate(['scramble-scoring'], navigationExtras);
      } else {
        MessageDisplayUtil.showErrorToast(
          'No scramble data found.',
          this.platform,
          this.toastCtl,
          3000,
          'bottom'
        );
      }
    } catch (error) {
      console.error('Error handling scramble flow:', error);
      MessageDisplayUtil.showErrorToast(
        'Failed to load scramble data.',
        this.platform,
        this.toastCtl,
        3000,
        'bottom'
      );
    } finally {
      this.onDismissPageLoader();
      this.playCompClicked = false;
    }
  }

  private getCompMatches(compId: number, roundNo: number = 1): Promise<CompetitionMatch[]> {
    return new Promise((resolve, reject) => {
      let _player: PlayerInfo;
      this.player$.pipe(take(1)).subscribe(player => {
        _player = player;
      });

      this.flightService.getCompMatches(compId, roundNo).subscribe(
        (compMatches: CompetitionMatch[]) => {
          console.debug('get comp matches', compMatches);

          const currentPlayerMatches = compMatches?.filter(
            (cm: CompetitionMatch) =>
              cm?.participants?.some(
                (p: CompetitionMatchParticipant) => p?.player?.id === _player?.playerId
              ) &&
              (cm?.status === 'InProgress' ||
                cm?.status === 'Pending' ||
                cm?.status === 'Completed')
          );

          const sortedMatches = currentPlayerMatches
            ?.filter(
              (pm: CompetitionMatch) => pm?.status !== 'Abandoned' && pm?.status !== 'Submitted'
            )
            .sort((a: CompetitionMatch, b: CompetitionMatch) => {
              if (a?.status === 'Completed' && b?.status === 'Pending') {
                return -1;
              } else if (a?.status === 'Pending' && b?.status === 'Completed') {
                return 1;
              } else if (a?.status === 'InProgress' && b?.status === 'Pending') {
                return -1;
              } else if (a?.status === 'Pending' && b?.status === 'InProgress') {
                return 1;
              } else {
                if (a?.startTime < b?.startTime) {
                  return -1;
                } else if (a?.startTime > b?.startTime) {
                  return 1;
                } else {
                  return 0;
                }
              }
            });
          resolve(sortedMatches || []);
        },
        (error: any) => {
          reject(error);
        }
      );
    });
  }

  private getCompTeamRoundScores(compId: number, roundNo: number = 1) {
    return new Promise((resolve, reject) => {
      let _player: PlayerInfo;
      this.player$.pipe(take(1)).subscribe(player => {
        _player = player;
      });

      this.flightService
        .getTeamRoundScoresByPlayerFlight(compId, roundNo, _player?.playerId)
        .subscribe(
          (teamRoundScores: CompetitionTeamRoundFlight) => {
            if (teamRoundScores) resolve(teamRoundScores);
            else reject(false);
          },
          error => {
            reject(error);
          }
        );
    });
  }

  private async isLiveScoringBlocked(compId: number): Promise<boolean> {
    if (!compId) {
      return false;
    }
    try {
      const compData: CompetitionData = await firstValueFrom(
        this.flightService.getCompetitionDetails(Number(compId))
      );
      return !!compData?.addlSettings?.disableLiveScoring;
    } catch (error) {
      console.error('Error checking disableLiveScoring:', error);
      return false;
    }
  }

  private checkAndDisplayConnection(): boolean {
    if (this.connService.getCurrentNetworkStatus()) {
      return true;
    } else {
      const msg = 'You have no internet connection. Cannot proceed';
      MessageDisplayUtil.showErrorToast(msg, this.platform, this.toastCtl, 5000, 'bottom');
      return false;
    }
  }

  private onDismissPageLoader() {
    if (this.pageLoader) {
      this.pageLoader.dismiss().then();
    }
    this.pageLoader = null;
  }

  async getActiveCompetitionRound(compId: number) {
    const compData: CompetitionData = await firstValueFrom(
      this.flightService.getCompetitionDetails(Number(compId))
    );

    const activeRound = compData?.gameRounds?.find(gr => {
      return gr.status === 'InProgress';
    });

    // let activeRound = {
    //   roundNo: 1,
    // };

    return activeRound;
  }
}
